import React from 'react'
import { IAdminService } from 'models/services'
import styles from './BookingSlotPicker.module.scss'

interface BookingSlotPickerProps {
  service?: IAdminService
  booked: Record<string, number> // ISO datetime → занято мест
  taken: Set<string> // полностью занятые слоты
  value: string | null // выбранный слот (ISO)
  onChange: (iso: string | null) => void
  seats: number
  onSeatsChange: (n: number) => void
}

const WEEK_LABELS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс']
const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
]

const pad = (n: number) => String(n).padStart(2, '0')
const toDateStr = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

// Логика слотов повторяет веб (ServiceBookingBlock + BookingCalendar):
// слоты берутся из расписания услуги по дню недели, занятость — из booked/taken.
export const BookingSlotPicker: React.FC<BookingSlotPickerProps> = ({
  service,
  booked,
  taken,
  value,
  onChange,
  seats,
  onSeatsChange
}) => {
  const schedule = service?.slots
  const seatCap = service?.capacity && service.capacity > 0 ? service.capacity : 1

  const today = React.useMemo(() => {
    const t = new Date()
    t.setHours(0, 0, 0, 0)
    return t
  }, [])
  const todayStr = toDateStr(today)

  const [viewDate, setViewDate] = React.useState(() => new Date(today))
  const [date, setDate] = React.useState('')

  // Есть ли у дня хотя бы один свободный (будущий, не занятый) слот
  const dayHasSlot = (d: Date) => {
    if (!schedule?.length) return false
    const jsDay = d.getDay()
    const weekday = jsDay === 0 ? 7 : jsDay
    const times = schedule.find((s) => s.weekday === weekday)?.times ?? []
    const now = Date.now()
    return times.some((time) => {
      const start = new Date(`${toDateStr(d)}T${time}:00`)
      return start.getTime() > now && !taken.has(start.toISOString())
    })
  }

  // 6 недель подряд начиная с понедельника на/до 1 числа
  const cells = React.useMemo(() => {
    const year = viewDate.getFullYear()
    const month = viewDate.getMonth()
    const offset = (new Date(year, month, 1).getDay() + 6) % 7
    const start = new Date(year, month, 1 - offset)
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      return d
    })
  }, [viewDate])

  // Слоты выбранной даты из расписания услуги
  const slots = React.useMemo(() => {
    if (!date || !schedule?.length) return []
    const jsDay = new Date(`${date}T00:00:00`).getDay()
    const weekday = jsDay === 0 ? 7 : jsDay
    const times = schedule.find((d) => d.weekday === weekday)?.times ?? []
    const now = Date.now()

    return [...times].sort().map((time) => {
      const start = new Date(`${date}T${time}:00`)
      const iso = start.toISOString()
      const remaining = Math.max(seatCap - (booked[iso] ?? 0), 0)
      return {
        iso,
        time: `${pad(start.getHours())}:${pad(start.getMinutes())}`,
        remaining,
        disabled: remaining <= 0 || start.getTime() < now
      }
    })
  }, [date, schedule, booked, seatCap])

  const selectedRemaining = React.useMemo(() => {
    const s = slots.find((x) => x.iso === value)
    return s ? s.remaining : 0
  }, [slots, value])

  const canGoPrev =
    viewDate.getFullYear() > today.getFullYear() ||
    (viewDate.getFullYear() === today.getFullYear() &&
      viewDate.getMonth() > today.getMonth())

  const changeMonth = (delta: number) =>
    setViewDate((p) => new Date(p.getFullYear(), p.getMonth() + delta, 1))

  const handleDateChange = (value: string) => {
    setDate(value)
    onChange(null)
    onSeatsChange(1)
  }

  const handleSlotSelect = (iso: string) => {
    onChange(iso)
    onSeatsChange(1)
  }

  if (!schedule?.length) {
    return (
      <div className={styles.empty}>
        У услуги нет расписания слотов — запись по слотам недоступна.
      </div>
    )
  }

  return (
    <div className={styles.body}>
      {/* календарь */}
      <div className={styles.left}>
        <div className={styles.header}>
          <span className={styles.monthLabel}>
            {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
          </span>
          <div className={styles.nav}>
            <button
              type='button'
              className={styles.navBtn}
              onClick={() => changeMonth(-1)}
              disabled={!canGoPrev}
              aria-label='Предыдущий месяц'
            >
              ‹
            </button>
            <button
              type='button'
              className={styles.navBtn}
              onClick={() => changeMonth(1)}
              aria-label='Следующий месяц'
            >
              ›
            </button>
          </div>
        </div>

        <div className={styles.grid}>
          {WEEK_LABELS.map((w) => (
            <div key={w} className={styles.weekLabel}>
              {w}
            </div>
          ))}

          {cells.map((d) => {
            const dateStr = toDateStr(d)
            const outside = d.getMonth() !== viewDate.getMonth()
            const disabled = outside || d < today || !dayHasSlot(d)
            const isSelected = dateStr === date
            const isToday = dateStr === todayStr
            return (
              <button
                key={dateStr}
                type='button'
                disabled={disabled}
                className={[
                  styles.day,
                  disabled ? styles.dayDisabled : '',
                  isSelected ? styles.daySelected : '',
                  isToday && !isSelected ? styles.dayToday : ''
                ].join(' ')}
                onClick={() => handleDateChange(dateStr)}
              >
                {d.getDate()}
              </button>
            )
          })}
        </div>
      </div>

      {/* слоты */}
      <div className={styles.right}>
        <div
          className={`${styles.slotsScroll} ${
            !date || slots.length === 0 ? styles.slotsScrollCentered : ''
          }`}
        >
          {!date && <div className={styles.slotsHint}>Выберите дату</div>}
          {date && slots.length === 0 && (
            <div className={styles.slotsHint}>Нет свободных слотов</div>
          )}
          {date && slots.length > 0 && (
            <div className={styles.slots}>
              {slots.map((slot) => (
                <button
                  key={slot.iso}
                  type='button'
                  className={`${styles.slot} ${
                    value === slot.iso ? styles.slotActive : ''
                  }`}
                  disabled={slot.disabled}
                  onClick={() => handleSlotSelect(slot.iso)}
                >
                  <span className={styles.slotTime}>{slot.time}</span>
                  {seatCap > 1 && (
                    <span className={styles.slotSeats}>
                      {slot.remaining > 0
                        ? `Осталось ${slot.remaining} мест`
                        : 'Мест нет'}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* счётчик мест — как на вебе, ограничен остатком по слоту */}
        {value && seatCap > 1 && selectedRemaining > 0 && (
          <div className={styles.seats}>
            <span className={styles.seatsLabel}>Мест:</span>
            <button
              type='button'
              className={styles.seatsBtn}
              onClick={() => onSeatsChange(Math.max(1, seats - 1))}
              disabled={seats <= 1}
            >
              −
            </button>
            <span className={styles.seatsValue}>{seats}</span>
            <button
              type='button'
              className={styles.seatsBtn}
              onClick={() => onSeatsChange(Math.min(selectedRemaining, seats + 1))}
              disabled={seats >= selectedRemaining}
            >
              +
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default BookingSlotPicker
